import { getAdminToken } from "./adminClient";
import { joinPath } from "./client";

async function analyticsFetch(path, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath(path), { headers, signal: options.signal });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

/** All Analytics page data in one request (preferred on mount). */
export function fetchAnalyticsDashboard(filterType = "months", options = {}) {
  return analyticsFetch(
    `v2/analytics/dashboard?filter_type=${encodeURIComponent(filterType)}`,
    options
  );
}

/** Fast KPIs + chart — load first on Analytics / Portfolio operations lens. */
export function fetchAnalyticsDashboardCore(filterType = "months", options = {}) {
  return analyticsFetch(
    `v2/analytics/dashboard-core?filter_type=${encodeURIComponent(filterType)}`,
    options
  );
}

/** Participant table + drop-off funnels — load after core. */
export function fetchAnalyticsDashboardExtended(options = {}) {
  return analyticsFetch("v2/analytics/dashboard-extended", options);
}

/** Live portfolio pulse for NEXUS dashboard home. */
export function fetchAnalyticsPulse(options = {}) {
  return analyticsFetch("v2/analytics/pulse", options);
}

export function fetchInviteAnalytics(options = {}) {
  return analyticsFetch("v2/analytics/inviteanalytics", options);
}

export function fetchSurveyAnalytics(options = {}) {
  return analyticsFetch("v2/analytics/survey_analytics", options);
}

export function fetchSurveyAnalyticsTable(options = {}) {
  return analyticsFetch("v2/analytics/survey_analyticsTable", options);
}

export function fetchStatusDistribution(options = {}) {
  return analyticsFetch("v2/analytics/v1/GETstatusdistribution", options);
}

export function fetchChartData(filterType = "months", options = {}) {
  return analyticsFetch(
    `v2/analytics/v1/GETchartdata?filter_type=${encodeURIComponent(filterType)}`,
    options
  );
}

export function fetchInsights({ dateFrom = "", dateTo = "", includeScores = true } = {}, options = {}) {
  const params = new URLSearchParams();
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  if (!includeScores) params.set("include_scores", "0");
  const qs = params.toString();
  return analyticsFetch(`v2/insights/v1/GETinsights${qs ? `?${qs}` : ""}`, options);
}

export function fetchInsightsScores({ dateFrom = "", dateTo = "" } = {}, options = {}) {
  const params = new URLSearchParams();
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  const qs = params.toString();
  return analyticsFetch(`v2/insights/scores${qs ? `?${qs}` : ""}`, options);
}

export async function fetchAnalyticsNarrative(context, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/analytics/narrative"), {
    method: "POST",
    headers,
    body: JSON.stringify({ context }),
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function fetchInsightsNarrative(context, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/insights/narrative"), {
    method: "POST",
    headers,
    body: JSON.stringify({ context }),
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function fetchPlatformChat({ message, hints, context, sessionId }, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/platform/chat"), {
    method: "POST",
    headers,
    body: JSON.stringify({
      message,
      hints: hints || {},
      context: context || {},
      session_id: sessionId || null,
    }),
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function fetchPlatformChatSession(sessionId, options = {}) {
  const token = getAdminToken();
  const headers = {};
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath(`v2/platform/chat/session/${encodeURIComponent(sessionId)}`), {
    method: "GET",
    headers,
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function fetchPlatformChatSessions(options = {}) {
  const token = getAdminToken();
  const headers = {};
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/platform/chat/sessions"), {
    method: "GET",
    headers,
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function fetchInsightsChat({ message, context, sessionId }, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/insights/chat"), {
    method: "POST",
    headers,
    body: JSON.stringify({ message, context, session_id: sessionId || null }),
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function fetchChartExplanation(context, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/insights/chart-explanation"), {
    method: "POST",
    headers,
    body: JSON.stringify({ context }),
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function fetchInsightsAnomalies(context, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/insights/anomalies"), {
    method: "POST",
    headers,
    body: JSON.stringify({ context }),
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function fetchBoardBrief(context, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/insights/board-brief"), {
    method: "POST",
    headers,
    body: JSON.stringify({ context }),
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function exportBoardBrief(brief, format, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/insights/board-brief/export"), {
    method: "POST",
    headers,
    body: JSON.stringify({ brief, format }),
    signal: options.signal,
  });
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok || contentType.includes("application/json")) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || `Export failed (${res.status})`);
  }
  return res.blob();
}

export async function fetchInsightsMemo(context, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/insights/memo"), {
    method: "POST",
    headers,
    body: JSON.stringify({ context }),
    signal: options.signal,
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || `Request failed (${res.status})`);
  }
  return json;
}

export async function exportPortfolioRollupPdf(payload, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/insights/portfolio-pdf"), {
    method: "POST",
    headers,
    body: JSON.stringify({ payload }),
    signal: options.signal,
  });
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.message || `PDF export failed (${res.status})`);
    }
    throw new Error(`PDF export failed (${res.status})`);
  }
  if (contentType.includes("application/json")) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || "PDF export failed");
  }
  return res.blob();
}

export async function exportAnalyticsReportPdf(payload, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/analytics/report-pdf"), {
    method: "POST",
    headers,
    body: JSON.stringify({ payload }),
    signal: options.signal,
  });
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.message || `PDF export failed (${res.status})`);
    }
    throw new Error(`PDF export failed (${res.status})`);
  }
  if (contentType.includes("application/json")) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || "PDF export failed");
  }
  return res.blob();
}

/** Build Insights portfolio performance URL with optional compare + auto-export. */
export function buildPortfolioPerformanceUrl(companyNames, { autoExport = true } = {}) {
  const names = [
    ...new Set((companyNames || []).map((n) => String(n || "").trim()).filter(Boolean)),
  ];
  if (names.length < 2) {
    throw new Error("Select at least 2 companies for a portfolio report comparison.");
  }
  if (names.length > 4) {
    throw new Error("Select up to 4 companies for a portfolio report.");
  }
  const params = new URLSearchParams();
  params.set("lens", "performance");
  params.set("compare", names.map((n) => encodeURIComponent(n)).join(","));
  if (autoExport) params.set("exportPortfolio", "1");
  return `/portfolio?${params.toString()}`;
}

/** Navigate to Insights compare view (optional auto-export on that page). */
export function navigateToPortfolioPerformance(companyNames, navigate, { autoExport = false } = {}) {
  const url = buildPortfolioPerformanceUrl(companyNames, { autoExport });
  if (typeof navigate === "function") {
    navigate(url);
    return { navigated: true };
  }
  window.location.assign(url);
  return { navigated: true };
}

export async function downloadPortfolioReportByCompanies(companyNames, options = {}) {
  const names = [
    ...new Set((companyNames || []).map((n) => String(n || "").trim()).filter(Boolean)),
  ];
  if (names.length < 2) {
    throw new Error("Select at least 2 companies for a portfolio report comparison.");
  }
  if (names.length > 4) {
    throw new Error("Select up to 4 companies for a portfolio report.");
  }
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  const res = await fetch(joinPath("v2/insights/portfolio-pdf-by-companies"), {
    method: "POST",
    headers,
    body: JSON.stringify({ company_names: names }),
    signal: options.signal,
  });
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.message || `PDF export failed (${res.status})`);
    }
    throw new Error(`PDF export failed (${res.status})`);
  }
  if (contentType.includes("application/json")) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || "PDF export failed");
  }
  return res.blob();
}

/** Open Insights portfolio roll-up PDF in a new tab (charts + full Insights export). */
export function openPePortfolioReport(companyNames) {
  const url = buildPortfolioPerformanceUrl(companyNames, { autoExport: true });
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    throw new Error("Allow pop-ups to open the portfolio report.");
  }
  return { openedInTab: true };
}
